import { useState } from 'react';
import { IconNames, BrandColors, SemanticColors } from '@sendible/design-system/src/theme';
import { Chip, Container } from './index.styles';
import { Icon } from '../../atoms/Icon';
import { Text } from '../../atoms/Text';
import { Button } from '../Button';

export interface FilterChipSubItem {
  id: string;
  label: string;
  icon?: IconNames;
  iconColor?: BrandColors | SemanticColors;
  imageUrl?: string;
  showAsAvatar?: boolean;
  avatarColor?: string;
  avatarLabel?: string;
}

export interface FilterChipData {
  id: string;
  label: string;
  items: FilterChipSubItem[];
  selectedIds: string[];
}

export interface FilterChipsProps extends Component {
  chips: FilterChipData[];
  onRemove?: (chipId: string) => void;
}

const MAX_VISIBLE = 3;

const renderChipContent = (chip: FilterChipData, failedImageIds: Record<string, true>, markImageFailed: (id: string) => void): React.ReactNode => {
  const { items, selectedIds } = chip;

  if (items.length === 0 && selectedIds.length > 0) {
    return <span>{selectedIds.length}</span>;
  }

  if (items.length > 0 && items.every((item) => item.showAsAvatar)) {
    const renderAvatar = (item: FilterChipSubItem) => {
      const initial = ((item.avatarLabel || item.label)?.trim()?.[0] ?? '?').toUpperCase();
      const canShowImg = Boolean(item.imageUrl) && !failedImageIds[item.id];

      return (
        <div
          className="avatar"
          key={`chip-avatar-${item.id}`}
          style={!canShowImg && item.avatarColor ? { background: item.avatarColor } : undefined}
        >
          {canShowImg ? (
            <img
              src={item.imageUrl}
              alt={initial}
              onError={() => markImageFailed(item.id)}
            />
          ) : (
            <Text
              variation="body_14"
              color="brandLight"
            >
              {initial}
            </Text>
          )}
        </div>
      );
    };

    if (items.length <= MAX_VISIBLE) return items.map(renderAvatar);

    return (
      <>
        {items.slice(0, 2).map(renderAvatar)}
        <span>+{items.length - 2}</span>
      </>
    );
  }

  if (items.length > 0 && items.every((item) => item.imageUrl)) {
    if (items.length <= MAX_VISIBLE) {
      return items.map((item) => (
        <img
          key={`chip-img-${item.id}`}
          src={item.imageUrl}
          alt={item.label[0] || ' '}
        />
      ));
    }

    return (
      <>
        {items.slice(0, 2).map((item) => (
          <img
            key={`chip-img-${item.id}`}
            src={item.imageUrl}
            alt={item.label[0] || ' '}
          />
        ))}
        <span>+{items.length - 2}</span>
      </>
    );
  }

  if (items.length > 0 && items.every((item) => item.icon)) {
    if (items.length <= MAX_VISIBLE) {
      return items.map((item) => (
        <Icon
          key={`chip-icon-${item.id}`}
          color={item.iconColor}
          name={item.icon as IconNames}
        />
      ));
    }

    return (
      <>
        {items.slice(0, 2).map((item) => (
          <Icon
            key={`chip-icon-${item.id}`}
            color={item.iconColor}
            name={item.icon as IconNames}
          />
        ))}
        <span>+{items.length - 2}</span>
      </>
    );
  }

  if (items.length > 0 && items.every((item) => item.avatarColor)) {
    const renderSmallAvatar = (item: FilterChipSubItem) => {
      const initial = ((item.avatarLabel || item.label)?.trim()?.[0] ?? '?').toUpperCase();

      return (
        <div
          className="avatar"
          key={`chip-avatar-${item.id}`}
          style={{ background: item.avatarColor }}
        >
          <Text
            variation="body_14"
            color="brandLight"
          >
            {initial}
          </Text>
        </div>
      );
    };

    if (items.length <= MAX_VISIBLE) return items.map(renderSmallAvatar);

    return (
      <>
        {items.slice(0, 2).map(renderSmallAvatar)}
        <span>+{items.length - 2}</span>
      </>
    );
  }

  if (items.length === 1 && chip.label === items[0].label) {
    return null;
  }

  if (items.length === 1) {
    return <span>{items[0].label}</span>;
  }

  return <span>{selectedIds.length}</span>;
};

export const FilterChips = ({ chips, className = '', customClass = '', onRemove, testId = 'filter-chips' }: FilterChipsProps) => {
  const [failedImageIds, setFailedImageIds] = useState<Record<string, true>>({});

  const markImageFailed = (id: string) => {
    setFailedImageIds((prev) => (prev[id] ? prev : { ...prev, [id]: true }));
  };

  return (
    <Container
      className={`filter-chips ${customClass} ${className}`}
      data-testid={testId}
    >
      {chips.map((chip) => {
        const content = renderChipContent(chip, failedImageIds, markImageFailed);

        return (
          <Chip
            data-testid={`${testId}-chip`}
            key={chip.id}
            $hasOnRemove={!!onRemove}
          >
            <p>{chip.label}</p>
            {content && <div>{content}</div>}
            {onRemove && (
              <Button
                data-testid={`${testId}-chip-close`}
                icon="close"
                onClick={() => onRemove(chip.id)}
                size={12}
              />
            )}
          </Chip>
        );
      })}
    </Container>
  );
};
